<template>
  <div id="collection">
    <!-- HEADER -->
    <section class="pt-lg-5 py-4">
      <div class="container">
        <div class="row">
          <div class="col-12 text-center">
            <section-header :title="collection.name" subtitle="Collection" />
            <div v-if="isLoggedIn && collection.is_public" class="link-wrapper">
              <div class="link-container w-100">
                <small><strong>Public Link:</strong></small>
                <div class="d-flex">
                  <input v-model="collectionLink" class="link" />
                  <div class="d-flex align-items-center">
                    <el-tooltip
                      effect="dark"
                      placement="top"
                      content="Link Copied"
                      manual
                      :value="collectionLinkCopied"
                    >
                      <button
                        v-clipboard:copy="collectionLink"
                        v-clipboard:success="onCopyCollectionLink"
                        class="copy-button"
                      >
                        <i class="far fa-clone"></i>
                      </button>
                    </el-tooltip>
                  </div>
                </div>
              </div>
            </div>
            <base-button
              v-if="
                isLoggedIn && user.id > 0 && user.id == $route.params.userId
              "
              @click="
                handleEditCollection(
                  collection.id,
                  collection.name,
                  collection.is_public
                )
              "
              class="mt-2"
              size="sm"
              type="primary"
              >EDIT COLLECTION</base-button
            >
          </div>
        </div>
      </div>
    </section>

    <!-- IMAGES -->
    <section class="py-4 pb-lg-6">
      <div class="container">
        <div class="row">
          <div class="images-wrapper col-12">
            <div class="row mt-6" v-if="loading">
              <div
                v-for="n in 12"
                :key="'collection-artwork-placeholder-' + n"
                class="col-md-3 col-6"
              >
                <artwork-card :placeholder="true" />
              </div>
            </div>
            <div v-else-if="collection.photos.length > 0">
              <div class="row artwork-section-header">
                <div
                  class="artwork-section-content col-12 d-flex justify-content-between align-items-center"
                >
                  <div>
                    <h4 class="m-0">IMAGES ({{ collection.photos.length }})</h4>
                  </div>
                </div>
              </div>
              <div class="row">
                <div
                  v-for="photo in collection.photos"
                  :key="'collection-artwork-' + photo.photo_id"
                  class="col-md-3 col-6"
                >
                  <artwork-card
                    :username="photo.handle"
                    :thumbnail="photo.url_path"
                    :artworkId="photo.photo_id"
                    :avatar="photo.profile_image"
                    :licensed="photo.licensed"
                    @imageRemovedFromCollection="getCollectionDetails"
                    :proFile="photo.pro_file_id ? true : false"
                    :isVerified="photo.is_verified === '1' ? true : false"
                    :isDisabled="
                      photo.is_disabled === '1' || photo.is_deactivated === '1'
                        ? true
                        : false
                    "
                  />
                </div>
              </div>
            </div>
            <div
              v-if="collection.animations.length > 0"
              class="images-wrapper col-12"
            >
              <div class="row artwork-section-header mt-4">
                <div
                  class="artwork-section-content col-12 d-flex justify-content-between align-items-center"
                >
                  <div>
                    <h4 class="m-0">
                      ANIMATIONS ({{ collection.animations.length }})
                    </h4>
                  </div>
                </div>
              </div>
              <div class="row">
                <div
                  v-for="animation in collection.animations"
                  :key="'collection-animation-' + animation.animation_id"
                  class="col-lg-3 col-md-4 col-6"
                >
                  <artwork-card
                    :username="animation.handle"
                    :thumbnail="animation.url_path"
                    :artworkId="animation.animation_id"
                    :poster="animation.poster_url_path"
                    :licensed="animation.licensed"
                    :avatar="animation.profile_image"
                    @imageRemovedFromCollection="getCollectionDetails"
                    type="animation"
                    :isVerified="animation.is_verified === '1' ? true : false"
                    :isDisabled="
                      animation.is_disabled === '1' ||
                      animation.is_deactivated === '1'
                        ? true
                        : false
                    "
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="
              collection.photos.length === 0 &&
              collection.animations.length === 0
            "
            class="images-wrapper col-12"
          >
            <div class="no-images text-center">
              You haven't added any artwork to this collection.
              <router-link
                to="/store"
                @click.native="resetSearch"
                class="btn btn-sm btn-outline-primary mt-3"
                >BROWSE AVAILABLE ARTWORK</router-link
              >
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { SectionHeader, ArtworkCard } from "../../components";
import { mapState } from "vuex";
const apiBaseURL = process.env.VUE_APP_API_URL;
const artgrabURL = process.env.VUE_APP_ARTGRAB_URL;

export default {
  name: "collection-page",
  metaInfo() {
    return {
      title: `Collection - ${this.getCollectionName()} - Art Grab`,
      meta: [
        {
          vmid: "twitter:title",
          name: "twitter:title",
          content: `Collection - ${this.getCollectionName()} - Art Grab`,
        },
        {
          vmid: "twitter:description",
          name: "twitter:description",
          content: `Check out this collection of exclusive artwork on Art Grab.`,
        },
        {
          vmid: "twitter:image",
          name: "twitter:image",
          content: `${this.getCollectionImage()}`,
        },
        {
          vmid: "og:url",
          property: "og:url",
          content: `${artgrabURL}/user/${this.collection.user_id}/collection/${this.collection.id}`,
        },
        {
          vmid: "og:title",
          property: "og:title",
          content: `Collection - ${this.getCollectionName()} - Art Grab`,
        },
        {
          vmid: "og:description",
          property: "og:description",
          content: `Check out this collection of exclusive artwork on Art Grab.`,
        },
        {
          vmid: "og:image",
          property: "og:image",
          content: `${this.getCollectionImage()}`,
        },
        {
          vmid: "description",
          property: "description",
          content: `Check out this collection of exclusive artwork on Art Grab.`,
        },
      ],
      link: [
        {
          rel: "canonical",
          href: `${artgrabURL}/user/${this.collection.user_id}/collection/${this.collection.id}`,
        },
      ],
    };
  },
  components: {
    SectionHeader,
    ArtworkCard,
  },
  data() {
    return {
      collection: {
        id: "",
        user_id: "",
        name: "",
        is_public: false,
        photos: [],
        animations: [],
      },
      loading: false,
      collectionLink: "",
      collectionLinkCopied: false,
    };
  },
  methods: {
    onCopyCollectionLink: function (e) {
      this.collectionLinkCopied = true;
      setTimeout(() => {
        this.collectionLinkCopied = false;
      }, 2000);
    },
    getCollectionImage() {
      if (this.collection.photos.length > 0 && this.collection.is_public) {
        return this.collection.photos[0].url_path;
      }
      return "https://i.imgur.com/gp9AfzO.jpeg";
    },
    getCollectionName() {
      if (this.collection.name) {
        return this.collection.name;
      }
      return "Collection";
    },
    resetSearch() {
      this.$store.commit("setSearchPage", 1);
    },
    handleEditCollection(id, name, is_public) {
      this.$store.commit("showEditCollectionModal", {
        status: true,
        name,
        id,
        is_public,
      });
    },
    getCollectionDetails() {
      this.loading = true;
      this.axios
        .request({
          url: `${apiBaseURL}/api/artv1.1/user/getcollectiondetails`,
          method: "post",
          data: {
            collection_id: this.$route.params.collectionId,
          },
        })
        .then((result) => {
          if (result.data.data.message === "Success") {
            const userId = this.$store.getters.user.id;
            if (
              result.data.data.is_public == 1 ||
              (userId > 0 && userId == this.$route.params.userId)
            ) {
              this.collection.id = result.data.data.id;
              this.collection.user_id = result.data.data.user_id;
              this.collection.name = result.data.data.name;
              this.collection.is_public =
                result.data.data.is_public == 1 ? true : false;

              this.collection.photos = result.data.data.photos;
              this.collection.animations = result.data.data.animations;

              if (this.collection.photos.length > 0) {
                for (let photo of this.collection.photos) {
                  photo.url_path = `${process.env.VUE_APP_CDN_BASE_URL}${photo.url_path}`;
                  photo.profile_image = `${process.env.VUE_APP_CDN_BASE_URL}${photo.profile_image}`;
                  if (photo.licensed == 1) {
                    photo.licensed = true;
                  } else {
                    photo.licensed = false;
                  }
                }
              }

              if (this.collection.animations.length > 0) {
                for (let animation of this.collection.animations) {
                  animation.url_path = `${process.env.VUE_APP_ANIMATION_CDN_URL}${animation.url_path}`;
                  animation.poster_url_path = `${process.env.VUE_APP_ANIMATION_CDN_URL}${animation.poster_url_path}`;
                  animation.profile_image = `${process.env.VUE_APP_CDN_BASE_URL}${animation.profile_image}`;
                  if (animation.licensed == 1) {
                    animation.licensed = true;
                  } else {
                    animation.licensed = false;
                  }
                }
              }
            } else {
              this.$router.push("/notfound");
            }
          }
        })
        .catch((error) => {
          /*eslint no-console: ["error", { allow: ["warn", "error"] }] */
          console.error(error);
        })
        .finally(() => (this.loading = false));
    },
  },
  mounted() {
    this.$store.commit("setLayout", "page-layout");
    if (!this.$route.params.collectionId) {
      this.$router.push("/noutfound");
    }
    this.getCollectionDetails();
    // this.collectionLink = artgrabURL + this.$route.path;
    this.collectionLink = window.location.href;
  },
  computed: {
    ...mapState(["user", "isLoggedIn"]),
  },
  watch: {
    user(newUser, oldUser) {
      if (newUser.collections) {
        this.getCollectionDetails();
      }
    },
  },
};
</script>

<style scoped lang="scss">
#collection {
  padding-top: 100px;

  .link-container {
    background: #f7f7f7;
    padding: 4px 10px 12px;
    border-radius: 3px;
  }

  .link-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    max-width: 400px;
    margin: 0 auto;

    .link {
      background-color: #e6e6e6;
      border: none;
      outline: none;
      border-radius: 3px;
      padding: 0.25rem 0.75rem;
      font-weight: 700;
      margin-right: 0.25rem;
      width: 100%;
    }

    .copy-button {
      background: none;
      border: none;
      outline: none;
      border-radius: 3px;
      padding: 0.25rem 0.5rem;

      &:hover {
        background: #e6e6e6;
      }

      &:active {
        background: #cccccc;
      }
    }
  }

  .artwork-section-header {
    margin-bottom: 40px;
    margin-left: 0;
    margin-right: 0;

    .artwork-section-content {
      padding: 0 0 10px;
      border-bottom: 1px solid #000;
    }
  }

  .no-images {
    padding: 6.844rem 1rem;
    background: #f7f7f7;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
}

@media (max-width: 991.98px) {
  #collection {
    padding-top: 100px;
  }
}
</style>
